import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isListNode, ListNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import { $getSelectionStyleValueForProperty, $isParentElementRTL } from '@lexical/selection';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { useTheme } from '@nexelem/deprecated-theme';
import {
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_NORMAL,
  KEY_MODIFIER_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlignDropDown } from '../../components/AlignDropDown';
import { BlockFormatDropDown, getBlockTypeToBlockName } from '../../components/BlockFormatDropDown';
import { Divider } from '../../components/Divider';
import { FontAppearanceFormSection } from '../../components/FontAppearance';
import { FONT_SIZE_OPTIONS, FontDropDown, LabelData } from '../../components/FontDropDown';
import { InsertDropDown } from '../../components/InsertDropDown';
import { UndoRedoSection } from '../../components/UndoRedoSection';

import { getSelectedNode } from '../../utils/getSelectedNode';
import { sanitizeUrl } from '../../utils/url';
import ActionsPlugin from '../ActionsPlugin';

export default function ToolbarPlugin() {
  const theme = useTheme();
  const { container } = theme.client.richTextField;
  const { getColor } = theme.palette;
  const { t: translation } = useTranslation();

  const blockTypeToBlockName = useMemo(() => getBlockTypeToBlockName(translation), [translation]);

  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] = useState<keyof ReturnType<typeof getBlockTypeToBlockName>>('paragraph');
  const [fontSize, setFontSize] = useState<LabelData>(FONT_SIZE_OPTIONS['15']);
  const [isLink, setIsLink] = useState(false);
  const [isRTL, setIsRTL] = useState(false);
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      // Update text format
      setIsRTL($isParentElementRTL(selection));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          if (type in blockTypeToBlockName) {
            setBlockType(type as keyof typeof blockTypeToBlockName);
          }
        }
      }
      // Handle buttons
      const fontSize = $getSelectionStyleValueForProperty(selection, 'font-size').replace(
        'px',
        '',
      ) as keyof typeof FONT_SIZE_OPTIONS;
      setFontSize(FONT_SIZE_OPTIONS[fontSize] || FONT_SIZE_OPTIONS['15']);
    }
  }, [activeEditor, blockTypeToBlockName]);

  useEffect(
    () =>
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          $updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    [editor, $updateToolbar],
  );

  useEffect(
    () =>
      mergeRegister(
        editor.registerEditableListener((editable) => {
          setIsEditable(editable);
        }),

        activeEditor.registerUpdateListener(({ editorState }) => {
          editorState.read(() => {
            $updateToolbar();
          });
        }),
      ),
    [$updateToolbar, activeEditor, editor],
  );

  useEffect(
    () =>
      activeEditor.registerCommand(
        KEY_MODIFIER_COMMAND,
        (payload) => {
          const event: KeyboardEvent = payload;
          const { code, ctrlKey, metaKey } = event;

          if (code === 'KeyK' && (ctrlKey || metaKey)) {
            event.preventDefault();
            return activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl('https://'));
          }
          return false;
        },
        COMMAND_PRIORITY_NORMAL,
      ),
    [activeEditor, isLink],
  );

  return (
    <div className="mb-2 flex justify-between border-b border-border-primary p-1">
      <div className="flex">
        <UndoRedoSection disabled={!isEditable} editor={activeEditor} />
        <Divider />
        {blockType in blockTypeToBlockName && activeEditor === editor && (
          <>
            <BlockFormatDropDown blockType={blockType} disabled={!isEditable} editor={editor} />
            <Divider />
          </>
        )}
        <FontDropDown disabled={!isEditable} editor={editor} type="font-size" value={fontSize} />
        <Divider />
        <AlignDropDown disabled={!isEditable} editor={activeEditor} isRTL={isRTL} />
        <Divider />
        <FontAppearanceFormSection disabled={!isEditable} editor={activeEditor} />
        <Divider />
        <InsertDropDown disabled={!isEditable} editor={editor} />
      </div>
      <ActionsPlugin />
    </div>
  );
}
